import { type MouseEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { r, s } from '@/theme/tokens';
import {
  Autocomplete,
  Box,
  CloseIcon,
  KeyboardArrowDownIcon,
  Text,
  TextField,
} from '@/ui/primitives';
import {
  buildFilterAutocompleteListboxSx,
  filterDropdownIndicatorIconSizePx,
  filterInputSx,
  mergeFilterSlotSx,
} from './filterControlStyles';
import { isFilterIndicatorTarget } from './filterIndicatorTargets';
import { buildTagsFilterDropdownPaperMotionSx } from './tagsFilterDropdownMotion';
import {
  resolveTagsFilterDropdownWidthPx,
  tagsFilterDropdownViewportMaxWidth,
} from './tagsFilterDropdownSizing';
import { TagsFilterFieldOption } from './TagsFilterFieldOption';
import { TagsFilterFieldSelectedValue } from './TagsFilterFieldSelectedValue';
import { useTagsFilterDropdownOpen } from './useTagsFilterDropdownOpen';
import type { TagOption, TagsFilterFieldProps } from './TagsFilterFieldTypes';

const filterPopperDataAttribute = { 'data-flowqa-filter-popper': 'true' } as const;
const selectLikeOptionBackground = 'rgba(255, 255, 255, 0.1)';
const selectLikeSelectedOptionBackground = 'rgba(0, 128, 128, 0.32)';
const defaultFilterWidthPx = 216;

const isAutocompleteDropdownTarget = (target: EventTarget): boolean =>
  target instanceof Element &&
  target.closest(
    '.MuiAutocomplete-option, .MuiAutocomplete-listbox, .MuiAutocomplete-paper, .MuiAutocomplete-popper',
  ) !== null;

const isSelectedTagRemoveTarget = (target: EventTarget): boolean =>
  target instanceof Element && target.closest('[data-flowqa-tag-chip-remove="true"]') !== null;

export const TagsFilterField = ({
  value,
  defaultValue = [],
  options,
  label = getFilterLabel('tags'),
  showLabel = true,
  fontSizePx = 15,
  optionFontSizePx = fontSizePx,
  labelPlacement = 'inline',
  labelControlGap,
  labelRole = 'body',
  labelColor = 'textPrimary',
  emptyLabel = getFilterEmptyLabel('allTags'),
  emptyOptionsAction,
  inputAriaLabel,
  fullWidth = false,
  controlWidthPx,
  dropdownWidthPx,
  inputBackgroundColor = 'var(--surface-page)',
  dropdownBackgroundColor = 'var(--surface-page)',
  inputLeftPadding = s('sm'),
  controlHeight = 32,
  controlRadius = r('md'),
  highlightActiveValue = true,
  showClearButton = true,
  multiple = true,
  optionPresentation = 'text',
  showSelectedChipRemoveButtons = false,
  renderOptionLabel,
  open,
  onOpenChange,
  onOptionSelect,
  onChange,
}: TagsFilterFieldProps): ReactElement => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const emptyOptionValue = '__flowqa_no_options__';
  const actionOptionValue = '__flowqa_tag_action__';
  const actionOption = { value: actionOptionValue, label: 'Add New' };
  const effectiveOptions = [
    ...(options.length > 0 ? options : [{ value: emptyOptionValue, label: 'No options' }]),
    ...(emptyOptionsAction ? [actionOption] : []),
  ];
  const selectedOptions = options.filter((option) => value.includes(option.value));
  const hasValue = selectedOptions.length > 0;
  const hasActiveFilterValue =
    highlightActiveValue &&
    (value.length !== defaultValue.length ||
      value.some((optionValue) => !defaultValue.includes(optionValue)));
  const activeBorderColor = 'primary.main';
  const shouldUseCompactSelectedInput = hasValue;
  const inputLeftInset = shouldUseCompactSelectedInput ? s('none') : inputLeftPadding;
  const fixedFilterWidth = `calc(${s('5xl')} + ${s('2xl')})`;
  const resolvedControlWidth = controlWidthPx ? `${controlWidthPx}px` : fixedFilterWidth;
  const optionRowHeight = '36px';
  const optionHorizontalPadding = s('md');
  const optionRightPadding = s('md');
  const optionCheckboxSlotWidth = `calc(${s('lg')} + ${s('sm')})`;
  const [measuredControlWidthPx, setMeasuredControlWidthPx] = useState(
    controlWidthPx ?? defaultFilterWidthPx,
  );
  const controlWidthValuePx = controlWidthPx ?? measuredControlWidthPx;
  const selectedInputWidth = hasValue ? '0px' : 'auto';
  const selectedInputFlex = hasValue ? '0 0 0px' : '1 1 auto';
  const endAdornmentPadding =
    selectedOptions.length > 0
      ? `calc(${s('2xl')} + ${s('md')})`
      : `calc(${s('2xl')} + ${s('lg')})`;
  const isInlineLabel = labelPlacement === 'inline';
  const shouldShowLabel = showLabel && label.length > 0;
  const showOptionChips = optionPresentation === 'tagChip';
  const hasCountTextColumn = options.some((option) => option.count !== undefined);
  const hasCountColumn = options.some(
    (option) => option.count !== undefined || option.countIcon !== undefined,
  );
  const hasCountIconColumn = options.some((option) => option.countIcon !== undefined);
  const optionGridTemplateColumns = hasCountColumn
    ? `${multiple ? `${optionCheckboxSlotWidth} ` : ''}minmax(0, 1fr) ${
        hasCountTextColumn ? 'max-content' : '0'
      }`
    : undefined;
  const countedDropdownWidthPx = resolveTagsFilterDropdownWidthPx({
    controlWidthPx: controlWidthValuePx,
    hasCountColumn,
    hasCountIconColumn,
    showOptionChips,
    viewportWidthPx: typeof window === 'undefined' ? undefined : window.innerWidth,
  });
  const measuredControlWidth = `${controlWidthValuePx}px`;
  const resolvedCustomDropdownWidthPx = dropdownWidthPx;
  const resolvedDropdownMinWidth =
    resolvedCustomDropdownWidthPx !== undefined
      ? `${String(resolvedCustomDropdownWidthPx)}px`
      : hasCountTextColumn
        ? `${String(countedDropdownWidthPx)}px`
        : measuredControlWidth;
  const resolvedDropdownWidth = resolvedDropdownMinWidth;
  const resolvedPopperWidth = `${resolvedDropdownWidth} !important`;
  const resolvedPopperMinWidth = `${resolvedDropdownMinWidth} !important`;
  const resolvedListboxWidth = hasCountColumn ? '100%' : resolvedDropdownWidth;
  const { isDropdownOpen, isDropdownClosing, setDropdownOpen, shouldRenderDropdown } =
    useTagsFilterDropdownOpen({ open, onOpenChange });
  const toggleDropdownFromControl = (event: MouseEvent<HTMLDivElement>): void => {
    if (
      isFilterIndicatorTarget(event.target) ||
      isAutocompleteDropdownTarget(event.target) ||
      isSelectedTagRemoveTarget(event.target)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (isDropdownOpen) {
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(true);
  };
  useEffect(() => {
    const element = controlRef.current;
    if (!element) {
      return;
    }

    const updateControlWidth = (): void => {
      const nextWidth = Math.round(element.getBoundingClientRect().width);
      if (nextWidth > 0) {
        setMeasuredControlWidthPx(nextWidth);
      }
    };

    updateControlWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateControlWidth);
      return () => {
        window.removeEventListener('resize', updateControlWidth);
      };
    }

    const observer = new ResizeObserver(() => {
      updateControlWidth();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [controlWidthPx, fullWidth]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const closeOnOutsidePointerDown = (event: globalThis.MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (controlRef.current?.contains(target)) {
        return;
      }
      if (
        target instanceof Element &&
        target.closest(
          '[data-flowqa-filter-popper="true"], .MuiAutocomplete-popper, .MuiAutocomplete-paper, .MuiAutocomplete-listbox',
        )
      ) {
        return;
      }
      setDropdownOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePointerDown);
    document.addEventListener('touchstart', closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointerDown);
      document.removeEventListener('touchstart', closeOnOutsidePointerDown);
    };
  }, [isDropdownOpen, setDropdownOpen]);

  const heightSx = {
    '& .MuiAutocomplete-inputRoot': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      maxHeight: `${controlHeight}px !important`,
      backgroundColor: inputBackgroundColor,
      alignItems: 'center',
      flexWrap: 'nowrap !important',
      overflow: 'hidden',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingRight: `${endAdornmentPadding} !important`,
      minWidth: 0,
      cursor: 'pointer',
      '& .MuiAutocomplete-endAdornment': {
        right: s('sm'),
        display: 'flex',
        alignItems: 'center',
        gap: s('none'),
        '& > button + button': {
          marginLeft: `calc(${s('xxs')} - ${s('md')}) !important`,
        },
      },
      '& .MuiAutocomplete-clearIndicator': {
        margin: `${s('none')} !important`,
        marginRight: `${s('none')} !important`,
        padding: `${s('xxs')} !important`,
        width: 'auto',
        height: 'auto',
        opacity: 1,
        visibility: 'visible',
        pointerEvents: 'auto',
      },
      '& .MuiAutocomplete-popupIndicator': {
        padding: `${s('xs')} !important`,
        margin: `${s('none')} !important`,
        marginRight: `${s('xs')} !important`,
        width: 'auto',
        height: 'auto',
        '& .MuiSvgIcon-root': {
          width: `${filterDropdownIndicatorIconSizePx}px`,
          height: `${filterDropdownIndicatorIconSizePx}px`,
        },
      },
    },
    '& .MuiAutocomplete-input': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      minWidth: shouldUseCompactSelectedInput ? '0px !important' : undefined,
      width: `${selectedInputWidth} !important`,
      flex: selectedInputFlex,
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingLeft: `${inputLeftInset} !important`,
      marginLeft: `${s('none')} !important`,
      lineHeight: '1 !important',
      cursor: 'pointer',
    },
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isInlineLabel ? 'row' : 'column',
        alignItems: isInlineLabel ? 'center' : 'stretch',
        gap: shouldShowLabel ? (isInlineLabel ? (labelControlGap ?? s('sm')) : s('sm')) : s('none'),
        minWidth: 0,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {shouldShowLabel ? (
        <Text
          role={labelRole}
          color={labelColor}
          transform="none"
          sx={{
            whiteSpace: isInlineLabel ? 'nowrap' : 'normal',
            fontSize: `${fontSizePx}px`,
            lineHeight: 1.4,
            fontWeight: (theme) => theme.typography.fontWeightRegular,
          }}
        >
          {label}
        </Text>
      ) : null}
      <Box
        ref={controlRef}
        onMouseDownCapture={toggleDropdownFromControl}
        sx={{
          width: fullWidth ? '100%' : resolvedControlWidth,
          minWidth: fullWidth ? 0 : resolvedControlWidth,
          maxWidth: fullWidth ? 'none' : resolvedControlWidth,
          flex: isInlineLabel ? (fullWidth ? '1 1 0' : `0 0 ${resolvedControlWidth}`) : undefined,
        }}
      >
        <Autocomplete
          multiple={multiple}
          disableCloseOnSelect={multiple}
          disableClearable={!showClearButton}
          disablePortal={false}
          fullWidth={false}
          open={shouldRenderDropdown}
          onOpen={() => {
            setDropdownOpen(true);
          }}
          onClose={(_event, reason) => {
            if (reason === 'blur') {
              return;
            }
            if (multiple && (reason === 'selectOption' || reason === 'removeOption')) {
              return;
            }
            setDropdownOpen(false);
          }}
          clearIcon={<CloseIcon fontSize="small" />}
          popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
          options={effectiveOptions}
          value={multiple ? selectedOptions : (selectedOptions[0] ?? null)}
          isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) => option.value === emptyOptionValue}
          onChange={(_event, nextValue) => {
            setDropdownOpen(multiple);
            const nextOptions: TagOption[] = Array.isArray(nextValue)
              ? (nextValue as TagOption[])
              : nextValue === null
                ? []
                : [nextValue as TagOption];
            if (nextOptions.length > 0) {
              const selectedOption = multiple
                ? nextOptions[nextOptions.length - 1]
                : nextOptions[0];
              if (
                selectedOption.value !== emptyOptionValue &&
                selectedOption.value !== actionOptionValue &&
                onOptionSelect?.(selectedOption.value) === false
              ) {
                setDropdownOpen(false);
                return;
              }
            }
            onChange(
              nextOptions
                .map((option) => option.value)
                .filter(
                  (optionValue) =>
                    optionValue !== emptyOptionValue && optionValue !== actionOptionValue,
                ),
            );
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <TagsFilterFieldOption
                actionOptionValue={actionOptionValue}
                emptyOptionsAction={emptyOptionsAction}
                emptyOptionValue={emptyOptionValue}
                hasCountColumn={hasCountColumn}
                multiple={multiple}
                option={option}
                optionCheckboxSlotWidth={optionCheckboxSlotWidth}
                optionGridTemplateColumns={optionGridTemplateColumns}
                optionFontSizePx={optionFontSizePx}
                optionKey={key}
                optionProps={optionProps}
                optionRowHeight={optionRowHeight}
                renderOptionLabel={renderOptionLabel}
                selected={selected}
                showOptionChips={showOptionChips}
                onActionClick={() => {
                  setDropdownOpen(false);
                }}
              />
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              fieldStyle="filter"
              inputHeight={controlHeight}
              inputBackgroundColor={inputBackgroundColor}
              placeholder={!hasValue ? emptyLabel : undefined}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
              inputProps={{
                ...params.inputProps,
                'aria-label': inputAriaLabel ?? label,
                readOnly: true,
                style: { cursor: 'pointer' },
              }}
            />
          )}
          sx={(theme) => ({
            ...(typeof filterInputSx === 'function' ? filterInputSx(theme) : {}),
            ...heightSx,
            width: '100%',
            minWidth: 0,
            '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              borderRadius: `${controlRadius} !important`,
              cursor: 'pointer',
              color: theme.palette.common.white,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: `${controlRadius} !important`,
              borderColor: hasActiveFilterValue ? activeBorderColor : undefined,
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
              {
                borderColor: hasActiveFilterValue ? activeBorderColor : undefined,
              },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: hasActiveFilterValue ? 1 : undefined,
            },
            '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              cursor: 'pointer',
              color: theme.palette.common.white,
            },
            '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
              {
                ...theme.typography.body1,
                fontSize: `${fontSizePx}px`,
                color: theme.palette.common.white,
                opacity: 1,
              },
          })}
          slotProps={{
            popper: {
              ...filterPopperDataAttribute,
              placement: 'bottom-start',
              popperOptions: {
                modifiers: [
                  {
                    name: 'flip',
                    enabled: true,
                    options: {
                      fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
                      padding: 16,
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      padding: 16,
                      mainAxis: false,
                      altAxis: true,
                    },
                  },
                ],
              },
              modifiers: [
                {
                  name: 'flip',
                  enabled: true,
                  options: {
                    fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
                    padding: 16,
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    padding: 16,
                    mainAxis: false,
                    altAxis: true,
                  },
                },
              ],
              sx: {
                width: resolvedPopperWidth,
                minWidth: resolvedPopperMinWidth,
                maxWidth: `${tagsFilterDropdownViewportMaxWidth} !important`,
                zIndex: (theme) => theme.zIndex.modal,
                '& .MuiAutocomplete-paper': {
                  minWidth: resolvedDropdownMinWidth,
                  width: resolvedDropdownWidth,
                  maxWidth: '100%',
                },
                '& .MuiAutocomplete-listbox': {
                  width: `${resolvedListboxWidth} !important`,
                },
                '& .MuiAutocomplete-option': {
                  ...(hasCountColumn
                    ? {
                        boxSizing: 'border-box',
                        display: 'grid !important',
                        gridTemplateColumns: optionGridTemplateColumns,
                        width: '100% !important',
                      }
                    : {}),
                },
              },
            },
            paper: {
              sx: {
                backgroundColor: dropdownBackgroundColor,
                border: '1px solid',
                borderColor: (theme) => theme.app.colors.border,
                borderRadius: `${controlRadius} !important`,
                overflow: 'hidden',
                boxShadow: 'none',
                backgroundImage: 'none',
                '--Paper-overlay': 'none',
                '--Paper-shadow': 'none',
                minWidth: resolvedDropdownMinWidth,
                width: resolvedDropdownWidth,
                maxWidth: tagsFilterDropdownViewportMaxWidth,
                mt: s('none'),
                ...buildTagsFilterDropdownPaperMotionSx(isDropdownClosing),
              },
            },
            clearIndicator: {
              sx: {
                margin: `${s('none')} !important`,
                marginRight: `${s('none')} !important`,
                padding: `${s('xxs')} !important`,
                width: 'auto',
                height: 'auto',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                color: 'common.white',
              },
            },
            listbox: {
              sx: mergeFilterSlotSx(buildFilterAutocompleteListboxSx(optionRowHeight), {
                py: s('none'),
                minWidth: resolvedDropdownMinWidth,
                width: resolvedListboxWidth,
                maxWidth: tagsFilterDropdownViewportMaxWidth,
                '& .MuiAutocomplete-option': {
                  paddingLeft: `${optionHorizontalPadding} !important`,
                  paddingRight: `${optionRightPadding} !important`,
                  backgroundColor: 'transparent !important',
                  color: 'text.primary',
                  cursor: 'pointer',
                  ...(hasCountColumn
                    ? {
                        boxSizing: 'border-box',
                        display: 'grid !important',
                        gridTemplateColumns: optionGridTemplateColumns,
                        width: '100% !important',
                      }
                    : {}),
                  whiteSpace: 'nowrap',
                },
                '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused': {
                  backgroundColor: `${selectLikeOptionBackground} !important`,
                },
                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: `${selectLikeSelectedOptionBackground} !important`,
                },
                '& .MuiAutocomplete-option[aria-selected="true"]:hover, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
                  {
                    backgroundColor: `${selectLikeSelectedOptionBackground} !important`,
                  },
              }),
            },
          }}
          renderValue={(selected) => {
            const selectedValues = Array.isArray(selected) ? selected : [selected];
            return (
              <TagsFilterFieldSelectedValue
                controlHeight={controlHeight}
                inputLeftPadding={inputLeftPadding}
                onRemoveValue={
                  showSelectedChipRemoveButtons
                    ? (optionValue) => {
                        onChange(value.filter((selectedValue) => selectedValue !== optionValue));
                      }
                    : undefined
                }
                selectedValues={selectedValues}
                showOptionChips={showOptionChips}
              />
            );
          }}
        />
      </Box>
    </Box>
  );
};

export type { TagOption, TagsFilterFieldProps };
